// src/components/HR/HRNotificationCenter.jsx - Centro notifiche per HR (STESSO SCHEMA ADMIN)
import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../firebase';
import { getAdminUnreadNotifications, markAdminNotificationAsRead } from '../../firebase';
import './HRNotificationCenter.css';

const HRNotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  // Carica le notifiche quando il componente si monta
  useEffect(() => {
    loadNotifications();
    
    // Polling per aggiornare le notifiche ogni 30 secondi
    const intervalId = setInterval(loadNotifications, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Gestisce il click fuori dal dropdown per chiuderlo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carica le notifiche dal database (STESSO SISTEMA ADMIN)
  const loadNotifications = async () => {
    if (!auth.currentUser) return;

    setIsLoading(true);
    try {
      console.log('HRNotificationCenter: Caricamento notifiche HR...');
      const hrNotifications = await getAdminUnreadNotifications(auth.currentUser.uid);
      console.log('HRNotificationCenter: Notifiche caricate:', hrNotifications);
      
      setNotifications(hrNotifications);
      setUnreadCount(hrNotifications.length);
      setError(null);
    } catch (err) {
      console.error('HRNotificationCenter: Errore nel caricamento delle notifiche:', err);
      setError('Impossibile caricare le notifiche');
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle del dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      loadNotifications();
    }
  };

  // Segna una singola notifica come letta
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAdminNotificationAsRead(notificationId, auth.currentUser.uid);
      
      // Rimuovi la notifica dalla lista locale
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (err) {
      console.error('Errore nel segnare la notifica come letta:', err);
    }
  };

  // Gestisce il click su una notifica
  const handleNotificationClick = async (notification) => {
    await handleMarkAsRead(notification.id);
    
    // Naviga alla sezione appropriata in base al tipo
    if (notification.type === 'new_report') {
      window.location.hash = '#hr-reports';
    } else if (notification.type === 'new_request') {
      window.location.hash = '#hr-requests';
    }
    
    setIsOpen(false);
  };

  // Formatta la data della notifica
  const formatNotificationDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Adesso';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min fa`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ore fa`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} giorni fa`;
    
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Ottieni icona in base al tipo di notifica
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_report':
        return '📝';
      case 'new_request':
        return '📋';
      case 'hours_discrepancy':
        return '⚠️';
      case 'long_shift':
        return '⏱️';
      case 'new_user':
        return '👤';
      default:
        return '🔔';
    }
  };

  return (
    <div className="hr-notification-center">
      <div className="notification-bell-wrapper" ref={bellRef}>
        <button
          className="notification-bell"
          onClick={toggleDropdown}
          title={`Notifiche HR ${unreadCount > 0 ? `(${unreadCount} non lette)` : ''}`}
        >
          <span className="bell-icon">🔔</span>
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Dropdown delle notifiche */}
      {isOpen && (
        <div className="notification-dropdown hr-theme" ref={dropdownRef}>
  <div className="notification-header">
  <h3>Notifiche HR</h3>
  <button
    className="hr-close-btn"
    onClick={() => setIsOpen(false)}
    aria-label="Chiudi notifiche"
    title="Chiudi"
  >
    ✕
  </button>
</div>

          <div className="notification-content">
            {isLoading ? (
              <div className="notification-loading">
                <div className="loading-spinner"></div>
                <p>Caricamento notifiche...</p>
              </div>
            ) : error ? (
              <div className="notification-error">
                <span className="error-icon">⚠️</span>
                <p>{error}</p>
                <button onClick={loadNotifications} className="retry-btn">
                  Riprova
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <span className="empty-icon">🔕</span>
                <p>Nessuna notifica</p>
                <small>Le notifiche HR appariranno qui</small>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="notification-item"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content-body">
                      <div className="notification-title">
                        {notification.title || 'Nuova notifica'}
                      </div>
                      <div className="notification-message">
                        {notification.message}
                      </div>
                      <div className="notification-time">
                        {formatNotificationDate(notification.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <small>
                {notifications.length === 1 
                  ? '1 notifica non letta' 
                  : `${notifications.length} notifiche non lette`}
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HRNotificationCenter;